// Header engine light — turns the action-item-email worker's heartbeat into
// the red/green "Claude Code CLI connected?" indicator next to "Main Agent
// Online". Pure — no React/DB — so it's unit-tested directly.
//
// Semantics (per Joel's feedback): the LIGHT is about the $0 Claude Code CLI —
//   GREEN  = CLI connected → drafts run free on the Claude subscription
//   RED    = CLI disconnected → drafting uses the paid Claude API (or nothing);
//            click the chip for step-by-step connect instructions
//   GRAY   = the send worker itself is offline/disabled (nothing to connect)
// The TEXT always states what's actually in use right now.
//
// The worker publishes { enabled, drafting_engine, cli_available, agentmail_ready }
// on its heartbeat info (owner-readable daemon_heartbeat:* key). cli_available
// is a separate probe (binary on PATH + runnable): CLAUDE_CLI_ENABLED with a
// missing binary must show RED, never a false green.

import { classifyHeartbeat, type HeartbeatRow } from '@reverra/shared/heartbeat/read';

export type EngineLight = 'green' | 'red' | 'gray';
export type EngineChip = {
  text: string;
  light: EngineLight;
  note: string;
  /** Show the step-by-step CLI connect guide in the popover. */
  showConnectSteps: boolean;
};

const SEND_NOT_READY =
  ' Heads-up: sending isn’t configured yet (AGENTMAIL_API_KEY missing) — drafts will generate but Send will error.';

export function describeDraftingEngine(row: HeartbeatRow | null, now: Date): EngineChip {
  if (!row || !row.payload || classifyHeartbeat(row.payload, now).status === 'alert') {
    return {
      text: 'Assistant offline',
      light: 'gray',
      note: 'The email assistant worker isn’t running right now, so drafting and sending are paused. Once the workers are back online this shows the live drafting engine.',
      showConnectSteps: false,
    };
  }

  const info = row.payload.info ?? {};
  if (info.enabled === false) {
    return {
      text: 'Email drafting off',
      light: 'gray',
      note: 'The worker is running but the email helper is switched off (ACTION_ITEM_EMAIL_ENABLED=false).',
      showConnectSteps: false,
    };
  }

  const sendNote = info.agentmail_ready === false ? SEND_NOT_READY : '';

  switch (info.drafting_engine) {
    case 'cli':
      if (info.cli_available === false) {
        return {
          text: 'Claude Code CLI · not found',
          light: 'red',
          note:
            'The CLI engine is enabled, but the `claude` binary isn’t installed (or isn’t on PATH) on the machine running the workers — drafts will fail until it’s installed and logged in.' +
            sendNote,
          showConnectSteps: true,
        };
      }
      // Green ONLY on a confirmed probe. cli_available is null for one beat
      // after a worker restart (the probe is async) — showing green during
      // that window would be exactly the false-green this light exists to
      // prevent, so report "checking" instead.
      if (info.cli_available !== true) {
        return {
          text: 'Claude Code CLI · checking…',
          light: 'gray',
          note:
            'The CLI engine is enabled; the worker is still verifying the `claude` binary is installed. This settles within ~a minute.' +
            sendNote,
          showConnectSteps: false,
        };
      }
      return {
        text: 'Claude Code CLI · $0',
        light: 'green',
        note:
          'Connected — drafts run free on the Claude subscription of the machine running the workers. (If that machine’s CLI session ever logs out, drafts fail with a clear auth error — rerun `claude /login` there.)' +
          sendNote,
        showConnectSteps: false,
      };
    case 'api':
      return {
        text: 'Claude API',
        light: 'red',
        note:
          'Drafting works, but it’s using the metered Claude API because the Claude Code CLI isn’t connected. Connect the CLI to draft for $0 on the Claude subscription instead.' +
          sendNote,
        showConnectSteps: true,
      };
    case 'none':
      return {
        text: 'No AI engine',
        light: 'red',
        note:
          'No drafting engine is configured — connect the Claude Code CLI (steps below) or set ANTHROPIC_API_KEY on the workers.' +
          sendNote,
        showConnectSteps: true,
      };
    default:
      return {
        text: 'Engine unknown',
        light: 'gray',
        note: 'The worker didn’t report which drafting engine it’s using.' + sendNote,
        showConnectSteps: false,
      };
  }
}
